import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import {
  UnidadeMedidaDeItemDTO,
  UnidadeMedidaDeItemFormDTO,
} from '../../models/configuracoes/unidade-de-medida.model';
import { UnidadeMedidaDeItemService } from '../../services/configuracoes/unidade-de-medida.service';
import { TableColumn } from '../../models/shared/table-column.model';

@Component({
  selector: 'app-unidade-de-medida',
  templateUrl: './unidade-de-medida.component.html',
  styleUrls: ['./unidade-de-medida.component.scss'],
  providers: [MessageService],
})
export class UnidadeDeMedidaComponent implements OnInit, OnDestroy {
  /**
   * Modo EMBUTIDO (Operar Cliente / Área do Contador): opera sob o CONTEXTO do
   * cliente (X-Empresa-Contexto). Esconde o page-header e oculta o excluir
   * (desvincular sai do escopo add/edit sob contexto). ADICIONAR unidade ao
   * cliente e EDITAR continuam disponíveis.
   */
  @Input() embutido = false;

  /** Id da empresa do CLIENTE (só no modo embutido). */
  @Input() empresaContextoId?: number | null;

  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  deleteDialogVisible = false;
  deleteItemId: string | null = null;

  /** IDs que nunca podem ser excluidos */
  private readonly idsProtegidos = new Set<number>([1]);

  // Objetos de traducao (padrao nova-despesa)
  texts = {
    titulo: '',
    subtitulo: '',
    selecionar_unidade: '',
    lista_unidades: '',
    nenhuma_unidade: '',
    ajuste_filtros: '',
    carregando: '',
    sucesso_adicionar: '',
    erro_adicionar: '',
    sucesso_excluir: '',
    erro_excluir: '',
    selecionar_obrigatorio: '',
    confirmacao: '',
    confirmar_exclusao: '',
    unidade_ja_existe: '',
    unidade_tem_itens: '',
    aba_cadastro: '',
    aba_lista: ''
  };

  labels = {
    codigo_dte: '',
    tipo_de_unidade: '',
    nome: '',
    ativo: '',
    inativo: '',
    casas_decimais: '',
    data_inclusao: '',
    acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    selecione_unidade: '',
    buscar: ''
  };

  botoes = {
    adicionar: '',
    confirmar: '',
    cancelar: '',
    nova_unidade: ''
  };

  tooltips = {
    excluir: ''
  };

  private langChangeSub!: Subscription;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'unidade-medida-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoDTE', labelKey: 'configuracao-unidade-de-medida.labels.codigo_dte', visible: true, fixed: false, defaultVisible: true },
    { key: 'tipoUnidade', labelKey: 'configuracao-unidade-de-medida.labels.tipo_de_unidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'nome', labelKey: 'configuracao-unidade-de-medida.labels.nome', visible: true, fixed: true, defaultVisible: true },
    { key: 'casasDecimais', labelKey: 'configuracao-unidade-de-medida.labels.casas_decimais', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'configuracao-unidade-de-medida.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'configuracao-unidade-de-medida.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  unidadeDeMedida: UnidadeMedidaDeItemDTO[] = [];
  unidadeDeMedidaEmpresa: UnidadeMedidaDeItemDTO[] = [];
  unidadeDeMedidaEmpresaOriginal: UnidadeMedidaDeItemDTO[] = [];
  first3 = 0;
  rows3 = 5;
  private _lastRows3: number = 5;
  totalRecords = 0;
  loading = true;
  lastfield: string = 'id';
  lastSort: string = 'asc';
  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];
  form: UnidadeMedidaDeItemFormDTO = this.createEmptyForm();
  filterName = '';
  private search$ = new Subject<string>();

  constructor(
    private messageService: MessageService,
    private unidadeMedidaDeItemService: UnidadeMedidaDeItemService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadColumnPreferences();
    this.loadTranslations();
    this.setupSearchDebounce();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
    this.findUnidadesAdicionaveis();
    this.findUnidadeDeMedidaEmpresa();
  }

  ngOnDestroy(): void {
    this.search$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  onTabChange(event: { index: number }): void {
    const prefix = 'configuracao-unidade-de-medida';
    if (event.index === 1) {
      this.texts.subtitulo = this.translate.instant(`${prefix}.texts.subtitulo_lista`);
    } else {
      this.texts.subtitulo = this.translate.instant(`${prefix}.texts.subtitulo`);
    }
  }

  private loadTranslations(): void {
    const prefix = 'configuracao-unidade-de-medida';
    const isListaAtiva = this.activeTabIndex === 1;

    this.texts = {
      titulo: this.translate.instant(`${prefix}.texts.titulo`),
      subtitulo: this.translate.instant(
        isListaAtiva ? `${prefix}.texts.subtitulo_lista` : `${prefix}.texts.subtitulo`
      ),
      selecionar_unidade: this.translate.instant(`${prefix}.texts.selecionar_unidade`),
      lista_unidades: this.translate.instant(`${prefix}.texts.lista_unidades`),
      nenhuma_unidade: this.translate.instant(`${prefix}.texts.nenhuma_unidade`),
      ajuste_filtros: this.translate.instant(`${prefix}.texts.ajuste_filtros`),
      carregando: this.translate.instant(`${prefix}.texts.carregando`),
      sucesso_adicionar: this.translate.instant(`${prefix}.texts.sucesso_adicionar`),
      erro_adicionar: this.translate.instant(`${prefix}.texts.erro_adicionar`),
      sucesso_excluir: this.translate.instant(`${prefix}.texts.sucesso_excluir`),
      erro_excluir: this.translate.instant(`${prefix}.texts.erro_excluir`),
      selecionar_obrigatorio: this.translate.instant(`${prefix}.texts.selecionar_obrigatorio`),
      confirmacao: this.translate.instant(`${prefix}.texts.confirmacao`),
      confirmar_exclusao: this.translate.instant(`${prefix}.texts.confirmar_exclusao`),
      unidade_ja_existe: this.translate.instant(`${prefix}.texts.unidade_ja_existe`),
      unidade_tem_itens: this.translate.instant(`${prefix}.texts.unidade_tem_itens`),
      aba_cadastro: this.translate.instant(`${prefix}.texts.aba_cadastro`),
      aba_lista: this.translate.instant(`${prefix}.texts.aba_lista`)
    };

    this.labels = {
      codigo_dte: this.translate.instant(`${prefix}.labels.codigo_dte`),
      tipo_de_unidade: this.translate.instant(`${prefix}.labels.tipo_de_unidade`),
      nome: this.translate.instant(`${prefix}.labels.nome`),
      ativo: this.translate.instant(`${prefix}.labels.ativo`),
      inativo: this.translate.instant(`${prefix}.labels.inativo`),
      casas_decimais: this.translate.instant(`${prefix}.labels.casas_decimais`),
      data_inclusao: this.translate.instant(`${prefix}.labels.data_inclusao`),
      acoes: this.translate.instant(`${prefix}.labels.acoes`),
      itens_por_pagina: this.translate.instant(`${prefix}.labels.itens_por_pagina`)
    };

    this.placeholders = {
      selecione_unidade: this.translate.instant(`${prefix}.placeholders.selecione_unidade`),
      buscar: this.translate.instant(`${prefix}.placeholders.buscar`)
    };

    this.botoes = {
      adicionar: this.translate.instant(`${prefix}.botoes.adicionar`),
      confirmar: this.translate.instant(`${prefix}.botoes.confirmar`),
      cancelar: this.translate.instant(`${prefix}.botoes.cancelar`),
      nova_unidade: this.translate.instant(`${prefix}.botoes.nova_unidade`)
    };

    this.tooltips = {
      excluir: this.translate.instant(`${prefix}.tooltips.excluir`)
    };
  }

  /** Busca unidades que a empresa ainda NAO possui (endpoint retorna array direto) */
  findUnidadesAdicionaveis() {
    this.unidadeMedidaDeItemService
      .listarUnidadesAdicionaveis()
      .subscribe({
        next: (response) => {
          this.unidadeDeMedida = response || [];
        },
        error: () => {
          this.unidadeDeMedida = [];
        }
      });
  }

  /** Verifica se uma unidade pode ser excluida (ID 1 e protegido globalmente) */
  podeExcluir(product: any): boolean {
    return !this.idsProtegidos.has(product.id);
  }

  findUnidadeDeMedidaEmpresa(
    page: number = 0,
    size: number = this.rows3,
    sort: string = 'id,asc'
  ) {
    this.loading = true;
    this.unidadeMedidaDeItemService
      .get('/unidaDeMedidaDeItemEmpresa', { page, size, sort })
      .subscribe({
        next: (response) => {
          this.unidadeDeMedidaEmpresaOriginal = response.content || [];
          this.unidadeDeMedidaEmpresa = [...this.unidadeDeMedidaEmpresaOriginal];
          this.totalRecords = response.totalElements || this.unidadeDeMedidaEmpresaOriginal.length;
          this.first3 = page * size;
          this.loading = false;

          // Reaplicar filtro local se houver termo de busca ativo
          if (this.filterName && this.filterName.trim().length > 0) {
            this.filtrarUnidadesLocal(this.filterName.trim());
          }
        },
        error: () => {
          this.unidadeDeMedidaEmpresaOriginal = [];
          this.unidadeDeMedidaEmpresa = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  onPageChange3(event: any) {
    const page = event.page;
    const size = event.rows;
    this.findUnidadeDeMedidaEmpresa(page, size);
  }

  onRowsPerPageChange() {
    if (this.rows3 === this._lastRows3) return;
    this._lastRows3 = this.rows3;
    this.first3 = 0;
    this.findUnidadeDeMedidaEmpresa(0, this.rows3);
  }

  customSort(event: SortEvent) {
    if (!event.field || !event.data) return;

    const field = event.field;
    const order = event.order ?? 1;
    this.lastfield = field;
    this.lastSort = order === 1 ? 'asc' : 'desc';

    event.data.sort((a: UnidadeMedidaDeItemDTO, b: UnidadeMedidaDeItemDTO) => {
      const valueA = this.getSortValue(a, field);
      const valueB = this.getSortValue(b, field);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return -1 * order;
      if (valueB == null) return 1 * order;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * order;
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
    });
  }

  /** Extrai valor para ordenacao, navegando por caminhos aninhados (ex: 'unidadeMedidaDeItemEmpresa.nome') */
  private getSortValue(item: UnidadeMedidaDeItemDTO, field: string): any {
    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  submit() {
    if (this.form.id === undefined) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.texts.selecionar_obrigatorio,
      });
      return;
    }

    this.unidadeMedidaDeItemService
      .post(`/unidaDeMedidaDeItemEmpresa?id=${this.form.id}`, undefined)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.texts.sucesso_adicionar,
          });
          this.form = this.createEmptyForm();
          this.findUnidadesAdicionaveis();
          this.findUnidadeDeMedidaEmpresa();
          this.activeTabIndex = 1;
        },
        error: (err) => {
          if (err.status === 403) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro'),
              detail: this.texts.unidade_ja_existe,
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro'),
              detail: this.texts.erro_adicionar,
            });
          }
        },
      });
  }

  deletarUnidadeMedida(id: string) {
    if (this.embutido) return; // DELETE (desvincular) fora do allowlist do contexto.
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (!this.deleteItemId) return;
    this.unidadeMedidaDeItemService
      .delete(`/unidaDeMedidaDeItemEmpresa/${this.deleteItemId}`)
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.texts.sucesso_excluir,
          });
          this.findUnidadesAdicionaveis();
          this.findUnidadeDeMedidaEmpresa();
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
        },
        error: (err) => {
          if (err.status === 403) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro'),
              detail: this.texts.unidade_ja_existe,
            });
          } else if (err.error?.message?.includes('Constraint') || err.error?.message?.includes('constraint')) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro'),
              detail: this.texts.unidade_tem_itens,
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro'),
              detail: this.texts.erro_excluir,
            });
          }
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
        },
      });
  }

  createEmptyForm(): UnidadeMedidaDeItemFormDTO {
    return {} as UnidadeMedidaDeItemFormDTO;
  }

  novaUnidade(): void {
    this.form = this.createEmptyForm();
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private setupSearchDebounce() {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.first3 = 0;
      if (term && term.trim().length > 0) {
        this.filtrarUnidadesLocal(term.trim());
      } else {
        this.unidadeDeMedidaEmpresa = [...this.unidadeDeMedidaEmpresaOriginal];
        this.totalRecords = this.unidadeDeMedidaEmpresaOriginal.length;
      }
    });
  }

  onSearchChange(value: string) {
    this.filterName = value || '';
    this.search$.next(this.filterName);
  }

  /** Filtra unidades de medida localmente buscando em todas as colunas visiveis */
  private filtrarUnidadesLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.unidadeDeMedidaEmpresa = this.unidadeDeMedidaEmpresaOriginal.filter(unidade => {
      const item = unidade as any;
      const unidadeItem = item.unidadeMedidaDeItemEmpresa || item;

      if (this.isColumnVisible('codigoDTE') && unidadeItem.codigoDTE) {
        if (String(unidadeItem.codigoDTE).toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('tipoUnidade') && unidadeItem.tipoUnidade) {
        if (String(unidadeItem.tipoUnidade).toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && unidadeItem.nome) {
        if (unidadeItem.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('ativo') && unidadeItem.ativo != null) {
        const ativoStr = unidadeItem.ativo ? 'ativo' : 'inativo';
        if (ativoStr.includes(termLower)) return true;
      }
      if (this.isColumnVisible('casasDecimais') && unidadeItem.casasDecimais != null) {
        if (String(unidadeItem.casasDecimais).includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && item.criadoEm) {
        const dataFormatada = new Date(item.criadoEm).toLocaleDateString('pt-BR');
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.unidadeDeMedidaEmpresa.length;
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  /** Retorna as unidades disponiveis para adicionar (ja filtradas pelo backend) */
  get unidadeDeMedidaDisponiveis(): UnidadeMedidaDeItemDTO[] {
    return this.unidadeDeMedida;
  }
}
